import type { ReactNode } from 'react'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { money } from '@/lib/format'
import { awaitingPayout, monthlyTrend, paidToDate, type CurrencyAmount } from '@/lib/payoutTrend'
import { useAsync } from '@/lib/useAsync'
import { PayoutChart } from '@/components/PayoutChart'
import { PageHeader } from '@/components/ui/page'
import { Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import type { PortalDealsResponse, PortalPayout, PortalStatement } from '@/types/api'

/**
 * What a partner opens the portal to find out: what they have been paid, and what is
 * coming.
 *
 * Deals answers neither. It lists what the customer signed, which is the input to the
 * money rather than the money - commission is earned when the customer pays Codemax, not
 * when they sign - so this page leads and Deals sits behind it.
 *
 * Three endpoints, all of which the portal already called before this page existed. There
 * is no dashboard endpoint and no figure here that the API computes: everything is summed
 * from rows a partner can already see one screen away, so nothing on this page can
 * disagree with the screen it summarises.
 */
export function Home() {
  const { partner } = useAuth()

  const payouts = useAsync<PortalPayout[]>(
    (signal) => api.get<PortalPayout[]>('/portal/payouts', undefined, signal),
    [],
  )
  const statements = useAsync<PortalStatement[]>(
    (signal) => api.get<PortalStatement[]>('/portal/statements', undefined, signal),
    [],
  )
  const deals = useAsync<PortalDealsResponse>(
    (signal) => api.get<PortalDealsResponse>('/portal/deals', undefined, signal),
    [],
  )

  const failed = payouts.error ?? statements.error ?? deals.error

  // Every panel needs all three, so the page waits for all three. Reading a total off two
  // loaded lists and one undefined one draws a figure that is wrong for as long as the
  // third is in flight, and nothing on screen says so.
  const loaded =
    payouts.data !== undefined && statements.data !== undefined && deals.data !== undefined

  if (!loaded && (payouts.loading || statements.loading || deals.loading)) return <Loading />

  if (failed) {
    return (
      <ErrorState
        title={failed.title}
        detail={failed.detail}
        onRetry={() => {
          payouts.reload()
          statements.reload()
          deals.reload()
        }}
      />
    )
  }

  const paid = paidToDate(payouts.data ?? [])
  const owed = awaitingPayout(statements.data ?? [])
  const signed = deals.data?.deals.length ?? 0

  // `new Date()` here rather than in the helper, so the window is decided once for the
  // render and every chart on the page covers the same months.
  const trends = monthlyTrend(payouts.data ?? [], new Date())

  return (
    <>
      <PageHeader
        title={greeting(partner?.displayName)}
        hint="Commission Codemax has paid you, and what is waiting to be settled."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Figure
          label="Paid to date"
          note={
            paid.length === 0
              ? 'No payouts yet'
              : `Across ${countOf(paid)} ${countOf(paid) === 1 ? 'payout' : 'payouts'}`
          }
        >
          <Amounts amounts={paid} />
        </Figure>

        <Figure
          label="Awaiting payout"
          note={
            owed.length === 0
              ? 'No statements issued yet'
              : `${countOf(owed)} ${countOf(owed) === 1 ? 'statement' : 'statements'} issued and unpaid`
          }
        >
          <Amounts amounts={owed} />
        </Figure>

        <Figure label="Signed deals" note="Accepted or won">
          <p className="tabular text-2xl font-semibold tracking-[-0.02em]">{signed}</p>
        </Figure>
      </div>

      {trends.length === 0 ? (
        <Card className="mt-3">
          <EmptyState
            title="Nothing paid out yet"
            hint={
              'Commission is earned when your customer pays Codemax, not when they sign. ' +
              'Once a statement is issued and settled, the months appear here.'
            }
          />
        </Card>
      ) : (
        trends.map((trend) => (
          <Card key={trend.currencyCode} className="mt-3">
            <CardHeader
              title="Monthly payouts"
              hint={`Twelve months to ${thisMonth()}.`}
              action={
                <span className="rounded bg-accent-wash px-1.5 py-0.5 font-mono text-[11px] font-semibold tracking-[0.08em] text-accent">
                  {trend.currencyCode}
                </span>
              }
            />
            <div className="px-5 py-4">
              <PayoutChart trend={trend} />
            </div>
          </Card>
        ))
      )}

      {trends.length > 1 && (
        <p className="mt-3 text-[12px] text-muted">
          A chart for each currency, never one across them. There are no exchange rates in
          this system, so your ringgit and your Singapore dollars are reported separately
          rather than converted into a single figure nobody agreed the rate for.
        </p>
      )}
    </>
  )
}

function Figure({ label, note, children }: { label: string; note: string; children: ReactNode }) {
  return (
    <Card className="px-5 py-4">
      <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-muted">
        {label}
      </p>
      <div className="mt-1.5">{children}</div>
      <p className="mt-1 text-[12px] text-muted">{note}</p>
    </Card>
  )
}

/**
 * One line per currency, each with its code. An em dash rather than a zero when there is
 * nothing: nil paid and nothing to pay are the same figure and different facts, and the
 * note underneath says which.
 */
function Amounts({ amounts }: { amounts: CurrencyAmount[] }) {
  if (amounts.length === 0) {
    return <p className="text-2xl font-semibold tracking-[-0.02em] text-faint">&mdash;</p>
  }

  return (
    <div className="space-y-0.5">
      {amounts.map((entry) => (
        <p key={entry.currencyCode} className="tabular text-2xl font-semibold tracking-[-0.02em]">
          <span className="mr-1 text-[0.62em] font-medium text-muted">{entry.currencyCode}</span>
          {money(entry.amount, entry.currencyCode)}
        </p>
      ))}
    </div>
  )
}

function countOf(amounts: CurrencyAmount[]): number {
  return amounts.reduce((sum, entry) => sum + entry.count, 0)
}

function thisMonth(): string {
  return new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

/**
 * The first name, because the whole display name is how somebody signs a form rather than
 * how they are greeted. A name that is one word greets by that word; no name at all still
 * gives the page a title.
 */
function greeting(displayName: string | undefined): string {
  const hour = new Date().getHours()
  const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const first = displayName?.trim().split(/\s+/)[0]

  return first ? `${part}, ${first}` : part
}
